import { CheckIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import { FC, Fragment } from 'react';

import { UNIT_TRANSFER_STAGES, UnitTransferStage } from 'utils/enums/unit-transfer-stage';

type StepState = 'complete' | 'current' | 'upcoming';

const getStepState = (stepIndex: number, stage: UnitTransferStage): StepState => {
  // READY_FOR_DIS sits in the last stage but is finished, so the final step reads as complete
  // rather than as something still in progress.
  if (stage.isComplete) return 'complete';
  if (stepIndex < stage.index) return 'complete';
  if (stepIndex === stage.index) return 'current';
  return 'upcoming';
};

const StageIndicator: FC<{ state: StepState; stepNumber: number }> = ({ state, stepNumber }) => (
  <span
    className={classNames(
      'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold',
      state === 'complete' && 'bg-indigo-600 text-white',
      state === 'current' && 'bg-white text-indigo-600 ring-2 ring-inset ring-indigo-600',
      state === 'upcoming' && 'bg-white text-gray-400 ring-1 ring-inset ring-gray-300'
    )}
  >
    {state === 'complete' ? <CheckIcon className="h-5 w-5" /> : stepNumber}
  </span>
);

/**
 * Collapses the backend's 16 statuses into the five phases an investor can actually name.
 * Hidden entirely once a request is closed early — the history below tells that story better
 * than a half-filled progress bar.
 */
const StageStepper: FC<{ stage: UnitTransferStage }> = ({ stage }) => {
  if (stage.isClosed) return null;

  const currentStage = UNIT_TRANSFER_STAGES[stage.index];

  return (
    <div>
      {/* Full stepper needs the horizontal room; below sm it collapses to a single line. */}
      <ol className="hidden sm:flex sm:items-center">
        {UNIT_TRANSFER_STAGES.map((step, stepIndex) => {
          const state = getStepState(stepIndex, stage);
          const isLast = stepIndex === UNIT_TRANSFER_STAGES.length - 1;

          return (
            <Fragment key={step.key}>
              <li className="flex flex-col items-center gap-2">
                <StageIndicator state={state} stepNumber={stepIndex + 1} />
                <span
                  className={classNames(
                    'text-xs',
                    state === 'current' ? 'font-semibold text-indigo-600' : 'text-gray-500'
                  )}
                >
                  {step.label}
                </span>
              </li>
              {!isLast ? (
                <li aria-hidden className="mb-6 h-0.5 flex-1">
                  <div
                    className={classNames(
                      'h-full w-full',
                      stepIndex < stage.index || stage.isComplete ? 'bg-indigo-600' : 'bg-gray-200'
                    )}
                  />
                </li>
              ) : null}
            </Fragment>
          );
        })}
      </ol>

      <p className="text-sm text-gray-600 sm:hidden">
        Step {stage.index + 1} of {UNIT_TRANSFER_STAGES.length}
        {currentStage ? ` — ${currentStage.label}` : ''}
      </p>
    </div>
  );
};

export default StageStepper;
